import { useCallback, useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import type { GitlabDetail, GitlabMergeOption } from '../lib/types';
import { pipelineTone } from '../lib/types';
import { formatDateTime } from '../lib/dates';

interface Props {
  itemId: string;
  projectId: string;
  itemKey: string;
  onTaskMoved: (movedTo: string) => void;
}

/**
 * The merge request on a task. GitLab keeps the diff and the discussion; this is the part a
 * tracker needs — is it open, does the pipeline pass, is it approved, and can I merge it now.
 */
export function GitlabPanel({ itemId, projectId, itemKey, onTaskMoved }: Props) {
  const [detail, setDetail] = useState<GitlabDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = useAsk();
  const [picking, setPicking] = useState(false);
  const [options, setOptions] = useState<GitlabMergeOption[] | null>(null);
  const [squash, setSquash] = useState(false);
  const [removeBranch, setRemoveBranch] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await api.get<GitlabDetail>(`/work-items/${itemId}/gitlab`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read GitLab');
      setDetail({ linked: false, canMerge: false });
    }
  }, [itemId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openPicker() {
    setPicking(true);
    setOptions(null);
    setError(null);
    try {
      setOptions(await api.get<GitlabMergeOption[]>(`/projects/${projectId}/gitlab/merge-requests`));
    } catch (err) {
      // Close the picker rather than leaving it on "Loading…" behind the error.
      setPicking(false);
      setError(err instanceof ApiError ? err.message : 'Could not list merge requests');
    }
  }

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  if (!detail) return <div className="card card-pad dim">Reading GitLab…</div>;

  const mr = detail.mergeRequest;
  const blockers = detail.mergeBlockers ?? [];

  return (
    <div className="card mt-2">
      <div className="card-head">
        <h3>Merge request</h3>
        {detail.linked && (
          <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={() => act(load)}>
            Refresh
          </button>
        )}
      </div>

      <div className="card-pad">
        {error && <div className="callout">{error}</div>}
        {notice && <div className="callout callout-ok">{notice}</div>}

        {!detail.linked && !picking && (
          <div className="row-between">
            <span className="dim">Nothing linked yet.</span>
            <button type="button" className="btn btn-sm" onClick={openPicker}>
              Link a merge request
            </button>
          </div>
        )}

        {picking && (
          <div className="stack-sm">
            <div className="field-label">Open merge requests</div>
            {options === null ? (
              <span className="dim">Loading…</span>
            ) : options.length === 0 ? (
              <span className="dim">No open merge requests in this repository.</span>
            ) : (
              options.map((option) => (
                <div key={option.iid} className="row-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                  <div className="grow">
                    <div className="row" style={{ gap: 7 }}>
                      <span className="key">!{option.iid}</span>
                      <span className="truncate" style={{ maxWidth: 260 }}>
                        {option.title}
                      </span>
                      {option.draft && <span className="badge">draft</span>}
                    </div>
                    <div className="dim mono" style={{ fontSize: 11 }}>
                      {option.sourceBranch} → {option.targetBranch}
                      {option.linkedTo ? ` · already on ${option.linkedTo}` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy || !!option.linkedTo}
                    onClick={() =>
                      act(async () => {
                        setDetail(await api.post<GitlabDetail>(`/work-items/${itemId}/gitlab/link`, { mergeIid: option.iid }));
                        setPicking(false);
                        setNotice(`!${option.iid} linked to ${itemKey}.`);
                      })
                    }
                  >
                    Link
                  </button>
                </div>
              ))
            )}
            <div>
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => setPicking(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {detail.linked && mr && (
          <div className="stack-sm">
            <div className="row-between">
              <a href={mr.webUrl} target="_blank" rel="noreferrer" className="row" style={{ gap: 8 }}>
                <span className="key">!{mr.iid}</span>
                <span className="truncate" style={{ maxWidth: 320, textDecoration: 'underline' }}>
                  {mr.title}
                </span>
              </a>
              <StateBadge state={mr.state} />
            </div>

            <div className="dim mono" style={{ fontSize: 11 }}>
              {mr.sourceBranch} → {mr.targetBranch} · {mr.author}
              {mr.changesCount ? ` · ${mr.changesCount} files` : ''}
            </div>

            <div className="row wrap" style={{ gap: 6 }}>
              {detail.pipeline ? (
                <a href={detail.pipeline.webUrl} target="_blank" rel="noreferrer">
                  <span className={`badge badge-${toneClass(pipelineTone(detail.pipeline.status))}`}>
                    pipeline {detail.pipeline.status}
                  </span>
                </a>
              ) : (
                <span className="badge">no pipeline</span>
              )}
              {detail.approvals && (
                <span className={detail.approvals.approved ? 'badge badge-ok' : 'badge'}>
                  {detail.approvals.approved
                    ? `approved${detail.approvals.approvedBy.length ? ` by ${detail.approvals.approvedBy.join(', ')}` : ''}`
                    : detail.approvals.left
                      ? `${detail.approvals.left} approval${detail.approvals.left === 1 ? '' : 's'} needed`
                      : 'not approved'}
                </span>
              )}
              {mr.hasConflicts && <span className="badge badge-accent">conflicts</span>}
              {mr.draft && <span className="badge badge-warn">draft</span>}
              {mr.commentCount > 0 && <span className="badge">{mr.commentCount} comments</span>}
            </div>

            {mr.state === 'opened' && detail.canMerge && (
              <div className="card card-pad" style={{ background: 'var(--surface-sunken)', marginTop: 6 }}>
                {blockers.length > 0 ? (
                  <>
                    <div className="field-label">Cannot merge yet</div>
                    <ul className="dim" style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12 }}>
                      {blockers.map((blocker) => (
                        <li key={blocker}>{blocker}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div className="field-label">Ready to merge</div>
                )}

                <div className="checkbox-row" style={{ marginBottom: 6 }}>
                  <input id="squash" type="checkbox" checked={squash} onChange={(e) => setSquash(e.target.checked)} />
                  <label htmlFor="squash" style={{ fontSize: 13 }}>
                    Squash commits
                  </label>
                </div>
                <div className="checkbox-row" style={{ marginBottom: 10 }}>
                  <input id="rmbranch" type="checkbox" checked={removeBranch} onChange={(e) => setRemoveBranch(e.target.checked)} />
                  <label htmlFor="rmbranch" style={{ fontSize: 13 }}>
                    Delete the source branch
                  </label>
                </div>

                <div className="row">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy || blockers.length > 0}
                    onClick={() =>
                      act(async () => {
                        const sure = await ask.confirm({
                          title: `Merge !${mr.iid} into ${mr.targetBranch}?`,
                          body: 'This merges the branch in GitLab for real, under your own account.',
                          confirmLabel: 'Merge it',
                        });
                        if (!sure) return;
                        const result = await api.post<{ merged: boolean; state: string; movedTo: string | null }>(
                          `/work-items/${itemId}/gitlab/merge`,
                          { squash, removeSourceBranch: removeBranch, moveToDone: true },
                        );
                        await load();
                        setNotice(
                          result.merged
                            ? `Merged${result.movedTo ? `, and ${itemKey} moved to ${result.movedTo}` : ''}.`
                            : `GitLab reports the state as ${result.state}.`,
                        );
                        if (result.movedTo) onTaskMoved(result.movedTo);
                      })
                    }
                  >
                    Merge
                  </button>
                  {detail.approvals && !detail.approvals.approved && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={busy}
                      onClick={() =>
                        act(async () => {
                          setDetail(await api.post<GitlabDetail>(`/work-items/${itemId}/gitlab/approve`));
                          setNotice('Approved in GitLab.');
                        })
                      }
                    >
                      Approve
                    </button>
                  )}
                </div>
              </div>
            )}

            {mr.state === 'opened' && !detail.canMerge && (
              <p className="dim" style={{ fontSize: 12, margin: 0 }}>
                Your role can see this but not merge it.
              </p>
            )}

            {mr.state === 'merged' && mr.mergedAt && (
              <p className="dim" style={{ fontSize: 12, margin: 0 }}>
                Merged {formatDateTime(mr.mergedAt)}.
              </p>
            )}

            <div>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                style={{ paddingLeft: 0 }}
                disabled={busy}
                onClick={() =>
                  act(async () => {
                    await api.del(`/work-items/${itemId}/gitlab/link`);
                    setDetail({ linked: false, canMerge: detail.canMerge });
                    setNotice('Unlinked.');
                  })
                }
              >
                Unlink
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: string }) {
  const cls = state === 'merged' ? 'badge badge-ok' : state === 'closed' ? 'badge' : 'badge badge-info';
  return <span className={cls}>{state}</span>;
}

function toneClass(tone: 'ok' | 'warn' | 'bad' | 'muted'): string {
  return tone === 'ok' ? 'ok' : tone === 'bad' ? 'accent' : tone === 'warn' ? 'warn' : 'info';
}
